const GA_MEASUREMENT_ID = 'G-T9SC3JT29D';
const GA_SCRIPT_ID = 'oblex-ga4-script';
const GA_DISABLE_FLAG = `ga-disable-${GA_MEASUREMENT_ID}`;
const ANALYTICS_COOKIE_PREFIXES = ['_ga', '_gid', '_gat'];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const setDisabledFlag = (value: boolean): void => {
  const globalWindow = window as unknown as Record<string, boolean>;
  globalWindow[GA_DISABLE_FLAG] = value;
};

const isIpAddress = (hostname: string): boolean => {
  return /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) || hostname.includes(':');
};

const getCookieDomainCandidates = (): string[] => {
  const hostname = window.location.hostname;
  if (!hostname || hostname === 'localhost' || isIpAddress(hostname)) {
    return [''];
  }

  const parts = hostname.split('.').filter(Boolean);
  const candidates = ['', `.${hostname}`];
  for (let index = 1; index < parts.length - 1; index += 1) {
    candidates.push(`.${parts.slice(index).join('.')}`);
  }

  return Array.from(new Set(candidates));
};

const expireCookie = (name: string, domain: string): void => {
  const attributes = [
    `${name}=`,
    'expires=Thu, 01 Jan 1970 00:00:00 GMT',
    'max-age=0',
    'path=/',
    'SameSite=Lax',
  ];

  if (domain) {
    attributes.push(`domain=${domain}`);
  }

  document.cookie = attributes.join('; ');
};

const clearAnalyticsCookies = (): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const cookieNames = document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('=')[0])
    .filter((name) =>
      ANALYTICS_COOKIE_PREFIXES.some((prefix) => name === prefix || name.startsWith(`${prefix}_`)),
    );

  for (const name of cookieNames) {
    for (const domain of getCookieDomainCandidates()) {
      expireCookie(name, domain);
    }
  }
};

const trackAcquisitionHints = (): void => {
  if (!window.gtag) {
    return;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const utmSource = searchParams.get("utm_source") ?? "";
  const utmMedium = searchParams.get("utm_medium") ?? "";
  const utmCampaign = searchParams.get("utm_campaign") ?? "";

  let referrerHost = "";
  if (document.referrer) {
    try {
      referrerHost = new URL(document.referrer).hostname;
    } catch {
      referrerHost = "";
    }
  }

  const sourceFingerprint = [utmSource, referrerHost].join("|").toLowerCase();
  const aiReferrer = /(chatgpt|openai|claude|anthropic|gemini|copilot|bing)/.test(sourceFingerprint);

  window.gtag("event", "traffic_source_hint", {
    utm_source: utmSource || "(none)",
    utm_medium: utmMedium || "(none)",
    utm_campaign: utmCampaign || "(none)",
    referrer_host: referrerHost || "(direct)",
    ai_referrer: aiReferrer ? "yes" : "no",
  });
};

export const disableAnalyticsTracking = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  setDisabledFlag(true);
  clearAnalyticsCookies();
};

export const loadAnalytics = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const existingScript = document.getElementById(GA_SCRIPT_ID);
  if (existingScript) {
    setDisabledFlag(false);
    return;
  }

  setDisabledFlag(false);
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    // Must be a regular function (not arrow) so GA4 receives the Arguments object
    // eslint-disable-next-line prefer-rest-params
    window.gtag = function gtag() { window.dataLayer!.push(arguments as unknown as unknown[]); };
  }

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID);
  trackAcquisitionHints();

  const script = document.createElement('script');
  script.id = GA_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
};
